// One map's soundscape + score for one run. Built when a run starts on that map, stopped (faded, sources stopped, graph
// disconnected) when the run ends or the map changes. Owns a lookahead scheduler: a 50 ms timer schedules everything due in
// the next 0.3 s (small batches; late steps are skipped, never piled up). The timer only exists while a run is on.
// Audio never feeds back into the game: it only reads night / storm / intensity handed to setState().
import type { MapAudioDef } from './mapAudioDef';
import { Ambience } from './ambience';
import { Score } from './score';
import { Kit } from './kit';
import { mulberry32 } from './noise';

export const LOOKAHEAD = 0.3;
const TICK_MS = 50;

export interface MapAudioOpts {
  music: boolean; amb: boolean;
  musicBus: AudioNode; ambBus: AudioNode;
  /** QA: no timer; the caller pumps (OfflineAudioContext rendering) */
  manual?: boolean;
  seed?: number;
}
export interface MapAudioStats { nodes: number; sources: number; eventVoices: number; peakEventVoices: number; events: number; notes: number }

export class MapAudio {
  readonly id: string;
  private kit: Kit;
  private amb: Ambience | null = null;
  private score: Score | null = null;
  private ambOut: GainNode | null = null;
  private musOut: GainNode | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;

  constructor(private ac: BaseAudioContext, def: MapAudioDef, o: MapAudioOpts) {
    this.id = def.id;
    let h = o.seed ?? 0; for (let i = 0; i < def.id.length; i++) h = (h * 31 + def.id.charCodeAt(i)) | 0;
    this.kit = new Kit(ac, mulberry32(o.seed !== undefined ? h : h ^ ((Math.random() * 0x7fffffff) | 0)));
    const t = ac.currentTime;
    if (o.amb) {
      this.ambOut = this.kit.gain(0); this.ambOut.connect(o.ambBus);
      this.ambOut.gain.setTargetAtTime(def.ambience.level, t, 0.8);
      this.amb = new Ambience(this.kit, def.ambience, this.ambOut);
    }
    if (o.music) {
      this.musOut = this.kit.gain(0); this.musOut.connect(o.musicBus);
      this.musOut.gain.setTargetAtTime(def.music.score.level, t, 0.5);
      this.score = new Score(this.kit, def.music.score, this.musOut);
    }
    this.pump();
    if (!o.manual) this.timer = setInterval(() => this.pump(), TICK_MS);
  }

  /** night 0..1 (live cycle crossfades), storm 0..1 (global fog hazards), intensity 0..1 (boss alive / overtime) */
  setState(night: number, storm: number, intensity: number): void {
    this.amb?.setState(night, storm); this.score?.setIntensity(intensity);
  }

  pump(): void {
    if (this.stopped) return;
    const now = this.ac.currentTime, h = now + LOOKAHEAD;
    this.amb?.pump(now, h); this.score?.pump(now, h);
  }

  /** fade out, stop every source, then disconnect the graph */
  stop(fade = 0.6): void {
    if (this.stopped) return; this.stopped = true;
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
    const t = this.ac.currentTime;
    this.ambOut?.gain.cancelScheduledValues(t); this.ambOut?.gain.setTargetAtTime(0, t, fade / 3);
    this.musOut?.gain.cancelScheduledValues(t); this.musOut?.gain.setTargetAtTime(0, t, fade / 3);
    this.kit.dispose(t + fade * 2.5);
  }
  get isStopped(): boolean { return this.stopped; }
  get scheduling(): boolean { return this.timer !== null; }

  stats(): MapAudioStats {
    return { nodes: this.kit.nodes.length, sources: this.kit.sources.length, eventVoices: this.amb?.voiceCount ?? 0,
      peakEventVoices: this.amb?.peakVoices ?? 0, events: this.amb?.played ?? 0, notes: this.score?.notes ?? 0 };
  }
}
